import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Hand, Loader2, Pencil, Undo2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import WorkflowStatusFilter, { WorkflowChip, effectiveWorkflow, getWorkflowConfig } from '@/components/WorkflowStatusChips';
import EstornoDialog from '@/components/EstornoDialog';
import PagamentoPagarme from '@/components/PagamentoPagarme';
import PagamentoBelluno from '@/components/PagamentoBelluno';
import ComprovantesPedido from '@/components/ComprovantesPedido';
import HistoricoPedido from '@/components/HistoricoPedido';
import { PageCover, PageBody, CoverSearch, SheetHeader, Segmented, StatusDot, PERIODOS, legendaPeriodo } from '@/components/Page';
import { API_URL } from '@/contexts/AuthContext';
import { linksDoPedido } from '@/lib/belluno';
import { podeEstornar } from '@/lib/estorno';
import { nomeFilial } from '@/lib/filiais';
import { formatBRL, formatBRLCompacto, formatNumero, formatData, formatDataHora, formatDia } from '@/lib/format';
import {
  FINANCIAMENTO, OPCOES_FORMA, aguardandoFinanceira, emMinusculas, formaDaParte, nomeDaForma, resumoFinanciamento,
  rotuloFormas, situacaoDaParte, tomDoPedido,
} from '@/lib/pagamento';

const FINALIZADOS = ['enviado_tiny', 'cancelado'];
// GET /pedidos devolve os 50 mais recentes; a busca e a forma de pagamento vão ao servidor para achar os antigos.
const LIMITE_LISTA = 50;

const tinyResumo = (p) =>
  (p.tiny_pedidos || [])
    .map((tp) => `${tp.filial_label || nomeFilial(tp.empresa_id)}${tp.numero_tiny ? `, pedido ${tp.numero_tiny}` : ''}`)
    .join('; ');

// Frase curta que acompanha o status: onde o pedido está e quem destrava.
const situacao = (p) => {
  if (p.envio_manual && !FINALIZADOS.includes(p.workflow_status)) return 'Envio manual, liberar no Financeiro';
  switch (effectiveWorkflow(p) || 'aguardando_pagamento') {
    case 'aguardando_pagamento': {
      if (aguardandoFinanceira(p)) return 'Aguardando a financeira';
      const formas = rotuloFormas(p);
      return `Cliente ainda não pagou${formas ? `, ${emMinusculas(formas)}` : ''}`;
    }
    case 'comprovante_em_analise': return 'Financeiro precisa conferir o comprovante';
    case 'pagamento_confirmado': return 'Pago, aguardando o envio ao Tiny';
    // Financiamento vai ao Tiny na aprovação, antes de a financeira pagar.
    case 'enviado_tiny': return aguardandoFinanceira(p) ? 'Aguardando a financeira' : tinyResumo(p) || 'No Tiny';
    case 'erro_tiny': return 'Tiny recusou, reprocessar em Erros Tiny';
    case 'revisao_antifraude': return p.antifraude_motivo || 'Aguardando a análise antifraude';
    case 'rejeitado': return 'Comprovante rejeitado pelo financeiro';
    case 'cancelado': return 'Pedido cancelado';
    default: return '';
  }
};

// Envio ao Tiny POR PEDIDO: manual = nenhum caminho automático envia; o pedido vai para a fila
// "Envio manual" do Financeiro e só sai no "Enviar ao Tiny" de lá.
const EnvioManualBotao = ({ pedido, onChanged }) => {
  const [salvando, setSalvando] = useState(false);
  if (FINALIZADOS.includes(pedido.workflow_status)) return null;
  const manual = Boolean(pedido.envio_manual);

  const alternar = async () => {
    let motivo = null;
    if (!manual) {
      // ponytail: prompt nativo — um campo opcional não justifica um dialog.
      motivo = window.prompt(
        'Passar para envio manual: o pedido só vai ao Tiny quando o Financeiro liberar.\n\nMotivo (opcional):',
        ''
      );
      if (motivo === null) return;
      motivo = motivo.trim();
      if (motivo.length > 300) {
        toast.error('Motivo muito longo (máximo 300 caracteres).');
        return;
      }
    } else if (!window.confirm('Voltar este pedido para o envio automático ao Tiny?')) {
      return;
    }
    setSalvando(true);
    try {
      const r = await axios.post(`${API_URL}/pedidos/${pedido.id}/envio-manual`, { manual: !manual, motivo });
      toast.success(r.data.message);
      onChanged?.();
    } catch (e) {
      // detail de validação (422) vem em lista: toast só recebe texto (objeto derruba a tela)
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Não foi possível alterar o envio');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Button size="sm" variant="outline" disabled={salvando} onClick={alternar}>
      {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Hand className="h-4 w-4" />}
      {manual ? 'Voltar para envio automático' : 'Passar para envio manual'}
    </Button>
  );
};

// Seção do documento: título pequeno em caixa alta, fio em cima. `lado` = duas
// seções dividem a mesma linha no desktop (ver Grupo).
const Secao = ({ titulo, children, className = '' }) => (
  <section className={`border-t border-border pt-5 ${className}`}>
    <h3 className="mb-3 text-[11.5px] font-semibold uppercase tracking-[0.05em] text-gray-500">{titulo}</h3>
    {children}
  </section>
);

// Duas seções lado a lado quando há largura; empilhadas no resto.
const Grupo = ({ children }) => <div className="mt-6 grid gap-6 lg:grid-cols-2">{children}</div>;

// Rótulo à esquerda, valor logo ao lado (não na outra ponta da folha).
const Linha = ({ rotulo, children }) => (
  <div className="grid grid-cols-[112px_minmax(0,1fr)] gap-x-4 py-1.5 text-[13.5px]">
    <dt className="text-gray-500">{rotulo}</dt>
    <dd className="min-w-0 font-medium [overflow-wrap:anywhere]">{children}</dd>
  </div>
);

// Linha de total: rótulo e valor nas pontas, valor tabular.
const LinhaTotal = ({ rotulo, children, forte = false, negativo = false }) => (
  <div className={`flex items-baseline justify-between gap-6 py-1.5 ${forte ? 'mt-1.5 border-t border-border pt-3' : ''}`}>
    <dt className={forte ? 'text-[15px] font-semibold' : 'text-[13.5px] text-gray-500'}>{rotulo}</dt>
    <dd className={`tabular-nums ${forte ? 'text-[17px] font-semibold' : negativo ? 'text-[13.5px] font-medium text-red-700 dark:text-red-300' : 'text-[13.5px] font-medium'}`}>{children}</dd>
  </div>
);

// Como o pedido é pago: a forma, ou as duas partes (cada uma com forma, valor e estado). Os comprovantes
// vêm logo depois, só para ver: aprovar e rejeitar ficam no Financeiro. No financiamento, a financeira e as
// parcelas que ela aprovou; o comprovante, se houver, é o do recebimento que o Financeiro registrou.
const SecoesPagamento = ({ pedido: p }) => {
  const partes = p.pagamento_split || [];
  const comps = p.comprovantes || [];
  const formas = rotuloFormas(p);
  const parcelas = !partes.length && p.forma_pagamento === 'cartao_credito' && p.parcelamento > 1 ? ` em ${p.parcelamento}x` : '';
  return (
    <>
      <Secao titulo="Pagamento" className="mt-6">
        <dl>
          {p.forma_pagamento === FINANCIAMENTO ? (
            <Linha rotulo="Forma">
              <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span>{resumoFinanciamento(p)}</span>
                {aguardandoFinanceira(p) && <StatusDot tone="run" className="text-[12.5px] font-normal">Aguardando a financeira</StatusDot>}
                {p.financiamento?.recebido_em && (
                  <StatusDot tone="ok" className="text-[12.5px] font-normal">Recebido em {formatData(p.financiamento.recebido_em)}</StatusDot>
                )}
              </span>
            </Linha>
          ) : partes.length ? (
            partes.map((pt) => {
              const s = situacaoDaParte(pt, p);
              return (
                <Linha key={pt.indice} rotulo={`Pagamento ${pt.indice}`}>
                  <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span>
                      {nomeDaForma(formaDaParte(pt))}, <span className="tabular-nums">{formatBRL(pt.valor)}</span>
                    </span>
                    <StatusDot tone={s.tom} className="text-[12.5px] font-normal">{s.texto}</StatusDot>
                  </span>
                </Linha>
              );
            })
          ) : (
            <Linha rotulo="Forma">{formas ? `${formas}${parcelas}` : 'Não informada'}</Linha>
          )}
        </dl>
      </Secao>
      {comps.length > 0 && (
        <Secao titulo={comps.length === 1 ? 'Comprovante' : `Comprovantes (${comps.length})`} className="mt-6">
          <ComprovantesPedido key={p.id} pedido={p} />
        </Secao>
      )}
    </>
  );
};

// O pedido como documento: identificação, valor, estado e ações no topo; o resto em seções.
const DocumentoPedido = ({ pedido: p, onEditar, onEstornar, onChanged }) => {
  const cfg = getWorkflowConfig(effectiveWorkflow(p));
  const frase = situacao(p);
  const end = p.endereco_entrega || {};
  const endereco = [
    [end.endereco || end.logradouro, end.numero].filter(Boolean).join(', '),
    end.complemento, end.bairro,
    [end.cidade, end.estado].filter(Boolean).join('/'),
    end.cep && `CEP ${end.cep}`,
  ].filter(Boolean).join(', ');
  const frete = p.frete || {};
  const prazo = frete.prazo || frete.prazo_entrega;
  const estornado = Number(p.valor_estornado) || 0;
  const manualAtivo = p.envio_manual && !FINALIZADOS.includes(p.workflow_status);

  return (
    <article className="p-6 sm:p-7">
      <p className="text-[12.5px] text-gray-500">
        {[p.numero_pedido, p.created_at && `criado ${formatDataHora(p.created_at)}`, p.created_by_name].filter(Boolean).join(' · ')}
      </p>
      <h2 className="mt-1.5 text-[22px] font-medium leading-tight tracking-[-0.01em]">{p.cliente_nome}</h2>
      <p className="mt-3.5 text-[36px] font-normal leading-none tracking-[-0.03em] tabular-nums">{formatBRL(p.total)}</p>
      <div className="mt-3">
        <StatusDot tone={tomDoPedido(p, cfg.tone)}>{frase ? `${cfg.label}, ${frase.charAt(0).toLowerCase()}${frase.slice(1)}` : cfg.label}</StatusDot>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={onEditar}>
          <Pencil className="h-4 w-4" />
          Editar
        </Button>
        <EnvioManualBotao pedido={p} onChanged={onChanged} />
        {podeEstornar(p) && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEstornar(p)}
            className="text-red-700 hover:text-red-800 dark:text-red-300"
            title="Estornar um pagamento: cartão pela Pagar.me ou link da Belluno"
          >
            <Undo2 className="h-4 w-4" />
            Estornar pagamento
          </Button>
        )}
      </div>
      {manualAtivo && (
        <p className="mt-3 text-[13px] text-amber-800 dark:text-amber-300">
          {['Envio manual', p.envio_manual_motivo, p.envio_manual_por && `por ${p.envio_manual_por}`].filter(Boolean).join(' · ')}
        </p>
      )}

      <SecoesPagamento pedido={p} />

      <Secao titulo="Produtos" className="mt-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11.5px] text-gray-500">
              <th scope="col" className="pb-2 text-left font-medium">Produto</th>
              <th scope="col" className="hidden pb-2 text-right font-medium sm:table-cell">Qtd</th>
              <th scope="col" className="hidden pb-2 text-right font-medium sm:table-cell">Unitário</th>
              <th scope="col" className="pb-2 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border border-y border-border">
            {(p.itens || []).map((it, i) => {
              const qtd = Number(it.quantidade) || 0;
              const unit = Number(it.preco_unitario) || 0;
              const desc = Number(it.desconto) || 0;
              return (
                <tr key={it.sku || i} className="align-top">
                  <td className="py-2.5 pr-4">
                    <p className="font-medium">{it.nome || it.nome_produto}</p>
                    <p className="mt-0.5 text-[12.5px] text-gray-500">
                      {[it.sku, `${qtd} ${qtd === 1 ? 'unidade' : 'unidades'}`].filter(Boolean).join(' · ')}
                      {desc > 0 && (
                        <>
                          {' · '}<s className="text-gray-400">{formatBRL(unit + desc)}</s>
                          <span className="ml-1.5 text-green-700 dark:text-green-400">−{formatBRL(desc)}</span>
                        </>
                      )}
                    </p>
                  </td>
                  <td className="hidden py-2.5 pl-4 text-right tabular-nums text-gray-500 sm:table-cell">{qtd}</td>
                  <td className="hidden py-2.5 pl-4 text-right tabular-nums text-gray-500 sm:table-cell">{formatBRL(unit)}</td>
                  <td className="py-2.5 pl-4 text-right font-medium tabular-nums">{formatBRL(qtd * unit)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Secao>

      <Grupo>
        <Secao titulo="Cliente">
          <dl>
            {p.cliente_telefone && <Linha rotulo="Telefone">{p.cliente_telefone}</Linha>}
            {p.cliente_email && <Linha rotulo="E-mail">{p.cliente_email}</Linha>}
            {p.cliente_cpf_cnpj && <Linha rotulo="CPF ou CNPJ">{p.cliente_cpf_cnpj}</Linha>}
            {!p.cliente_telefone && !p.cliente_email && !p.cliente_cpf_cnpj && <p className="text-[13.5px] text-gray-500">Sem dados de contato.</p>}
          </dl>
        </Secao>

        <Secao titulo="Entrega">
          <dl>
            <Linha rotulo="Transportadora">{frete.gratis ? 'Frete grátis' : frete.transportadora || 'Não informada'}</Linha>
            {(frete.tipo || frete.servico) && <Linha rotulo="Serviço">{frete.tipo || frete.servico}</Linha>}
            {prazo && <Linha rotulo="Prazo">{prazo} dias</Linha>}
            <Linha rotulo="Envio ao Tiny">{manualAtivo ? 'Manual' : 'Automático'}</Linha>
            {endereco && <Linha rotulo="Endereço">{endereco}</Linha>}
          </dl>
        </Secao>
      </Grupo>

      <Grupo>
        <Secao titulo="Totais">
          <dl>
            <LinhaTotal rotulo="Subtotal">{formatBRL(p.subtotal)}</LinhaTotal>
            {p.desconto_total > 0 && <LinhaTotal rotulo="Desconto" negativo>−{formatBRL(p.desconto_total)}</LinhaTotal>}
            <LinhaTotal rotulo="Frete">{frete.gratis ? 'Grátis' : formatBRL(frete.valor)}</LinhaTotal>
            {estornado > 0 && <LinhaTotal rotulo="Estornado" negativo>−{formatBRL(estornado)}</LinhaTotal>}
            <LinhaTotal rotulo="Total" forte>{formatBRL(p.total)}</LinhaTotal>
          </dl>
        </Secao>

        {(p.tiny_pedidos || []).length > 0 ? (
          <Secao titulo="No Tiny">
            <dl>
              {p.tiny_pedidos.map((tp, i) => (
                <Linha key={`${tp.empresa_id}-${i}`} rotulo={tp.filial_label || nomeFilial(tp.empresa_id)}>
                  {tp.numero_tiny ? `Pedido ${tp.numero_tiny}` : tp.status === 'criado' ? 'Criado' : <span className="text-red-700 dark:text-red-300">Com erro</span>}
                </Linha>
              ))}
            </dl>
          </Secao>
        ) : (
          <Secao titulo="No Tiny">
            <p className="text-[13.5px] text-gray-500">Ainda não enviado.</p>
          </Secao>
        )}
      </Grupo>

      {linksDoPedido(p).map(({ parte, titulo }) => (
        <Secao key={parte ?? 'pedido'} titulo={titulo} className="mt-6">
          <PagamentoBelluno pedido={p} parte={parte} onChanged={onChanged} />
        </Secao>
      ))}

      {/* Financiamento não passa pela Pagar.me: a seção só diria "sem cobrança" */}
      {p.forma_pagamento !== FINANCIAMENTO && (
        <Secao titulo="Dados do pagamento (Pagar.me)" className="mt-6">
          <PagamentoPagarme pedido={p} />
          {p.payment_link && (
            <p className="mt-3 break-all text-[12.5px] text-gray-500">
              Link de pagamento:{' '}
              <a href={p.payment_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline dark:text-blue-400">
                {p.payment_link}
              </a>
            </p>
          )}
        </Secao>
      )}

      <Secao titulo="Histórico" className="mt-6">
        <HistoricoPedido historico={p.historico || []} />
      </Secao>
    </article>
  );
};

const OrderManagement = () => {
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [kpisVersao, setKpisVersao] = useState(0); // estorno e envio manual mexem nos números da capa
  const [statusFilter, setStatusFilter] = useState('all');
  // Forma de pagamento, no servidor como a busca: com PIX, vêm os 50 pedidos com PIX mais recentes
  // (um pedido cartão + PIX entra no PIX, no cartão e em "Dois meios").
  const [forma, setForma] = useState('all');
  // Busca no servidor: a lista vem limitada aos 50 mais recentes e o alvo pode ser mais antigo.
  const [busca, setBusca] = useState('');
  // Recorte de data (mesma régua BRT dos KPIs do Dashboard e de Pedidos).
  const [periodo, setPeriodo] = useState('tudo');
  const [selecionadoId, setSelecionadoId] = useState(null);
  // Estorno de cartão: id do pedido em diálogo (o objeto vem da lista, fresco após refresh)
  const [estornoId, setEstornoId] = useState(null);
  const docRef = useRef(null);
  // Só a consulta mais recente escreve na lista: trocar de filtro rápido não deixa a resposta
  // atrasada do filtro anterior aparecer sob o chip novo. Enquanto ela não chega, a lista fica esmaecida.
  const ultimaConsulta = useRef(0);
  const [atualizando, setAtualizando] = useState(false);

  useEffect(() => {
    if (estornoId && !pedidos.some((p) => p.id === estornoId)) setEstornoId(null);
  }, [pedidos, estornoId]);

  const fetchPedidos = useCallback(async () => {
    const consulta = ++ultimaConsulta.current;
    setAtualizando(true);
    try {
      const params = {};
      if (busca.trim()) params.busca = busca.trim();
      if (periodo !== 'tudo') params.periodo = periodo;
      if (forma !== 'all') params.forma = forma;
      const response = await axios.get(`${API_URL}/pedidos`, { params });
      if (consulta === ultimaConsulta.current) setPedidos(response.data);
    } catch {
      if (consulta === ultimaConsulta.current) toast.error('Erro ao carregar pedidos');
    } finally {
      if (consulta === ultimaConsulta.current) {
        setLoading(false);
        setAtualizando(false);
      }
    }
  }, [busca, periodo, forma]);

  useEffect(() => {
    // Debounce só quando há busca digitada — carga inicial permanece imediata.
    const delay = busca ? 350 : 0;
    const timeoutId = setTimeout(() => fetchPedidos(), delay);
    return () => clearTimeout(timeoutId);
  }, [fetchPedidos, busca]);

  // Números da capa: base inteira do período (não só os 50 carregados).
  useEffect(() => {
    let ativo = true;
    axios
      .get(`${API_URL}/pedidos/kpis`, { params: { periodo } })
      .then((r) => ativo && setKpis(r.data))
      .catch(() => ativo && setKpis(null));
    return () => { ativo = false; };
  }, [periodo, kpisVersao]);

  const statusCounts = useMemo(() => {
    const counts = { all: pedidos.length };
    pedidos.forEach((p) => {
      const wf = effectiveWorkflow(p) || 'aguardando_pagamento';
      counts[wf] = (counts[wf] || 0) + 1;
    });
    return counts;
  }, [pedidos]);

  const filteredPedidos = useMemo(
    () => (statusFilter === 'all' ? pedidos : pedidos.filter((p) => effectiveWorkflow(p) === statusFilter)),
    [pedidos, statusFilter]
  );

  // Mantém a seleção quando a lista recarrega; sem seleção válida, abre o primeiro.
  const selecionado = filteredPedidos.find((p) => p.id === selecionadoId) || filteredPedidos[0] || null;

  const selecionar = (id) => {
    setSelecionadoId(id);
    if (window.innerWidth < 1280) docRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Lista vazia: diz todos os filtros que valem (a busca, a forma e o status, que é filtrado aqui na tela).
  const listaVazia = () => {
    const filtros = [
      forma !== 'all' && `com ${emMinusculas(OPCOES_FORMA[forma].label)}`,
      statusFilter !== 'all' && `no status "${getWorkflowConfig(statusFilter).label}"`,
    ].filter(Boolean).join(' e ');
    if (busca) return `Nenhum pedido encontrado para "${busca}"${filtros ? ` ${filtros}` : ''}.`;
    return `Nenhum pedido${filtros ? ` ${filtros}` : ''} neste período.`;
  };

  const figuras = kpis
    ? [
        { value: formatBRLCompacto(kpis.valor_total), label: 'vendidos no período' },
        { value: formatNumero(kpis.total), label: kpis.total === 1 ? 'pedido' : 'pedidos' },
        { value: formatNumero(kpis.enviados_tiny), label: 'já no Tiny' },
        { value: formatNumero(kpis.abertos), label: 'ainda não pagos' },
      ]
    : [];

  return (
    <>
      <PageCover
        title="Gestão de pedidos"
        subtitle={`${legendaPeriodo(periodo)}, todas as filiais`}
        actions={
          <>
            <Segmented options={PERIODOS} value={periodo} onChange={setPeriodo} label="Período" testid="gestao-periodo" onCover />
            <CoverSearch value={busca} onChange={setBusca} placeholder="Consultor, cliente ou número do pedido" data-testid="gestao-busca" />
          </>
        }
        figures={figuras}
      />
      <PageBody>
        {/* grid-cols-1 (minmax(0, 1fr)) no celular: sem ele a coluna cresce até a linha mais longa do documento */}
        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(300px,3fr)_minmax(0,7fr)]">
          <Card className="overflow-hidden">
            <SheetHeader title={loading ? 'Carregando pedidos' : `${formatNumero(filteredPedidos.length)} ${filteredPedidos.length === 1 ? 'pedido' : 'pedidos'}`}>
              <WorkflowStatusFilter
                value={statusFilter}
                onChange={setStatusFilter}
                counts={statusCounts}
                segundo={{ titulo: 'Forma de pagamento', opcoes: OPCOES_FORMA, value: forma, onChange: setForma, todos: 'Todas' }}
              />
            </SheetHeader>
            {loading ? (
              <ul aria-hidden>
                {Array.from({ length: 6 }).map((_, i) => (
                  <li key={i} className="space-y-2 border-b border-border px-5 py-4">
                    <span className="flex justify-between gap-4"><span className="h-4 w-40 animate-pulse rounded bg-muted" /><span className="h-4 w-20 animate-pulse rounded bg-muted" /></span>
                    <span className="block h-3 w-56 animate-pulse rounded bg-muted" />
                  </li>
                ))}
              </ul>
            ) : filteredPedidos.length === 0 ? (
              <p className={`px-5 py-12 text-sm text-gray-500 transition-opacity ${atualizando ? 'opacity-50' : ''}`}>{listaVazia()}</p>
            ) : (
              <ul
                className={`max-h-[calc(100vh-12rem)] overflow-y-auto transition-opacity ${atualizando ? 'opacity-50' : ''}`}
                aria-busy={atualizando}
                data-testid="gestao-lista"
              >
                {filteredPedidos.map((p) => {
                  const ativo = selecionado?.id === p.id;
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => selecionar(p.id)}
                        aria-current={ativo ? 'true' : undefined}
                        className={`grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1.5 border-b border-border px-5 py-3.5 text-left transition-colors duration-150 ${
                          ativo ? 'bg-blue-50 dark:bg-blue-500/10' : 'hover:bg-sheet2'
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium">{p.cliente_nome}</span>
                          <span className="block truncate text-[12.5px] text-gray-500">
                            {[p.numero_pedido, formatDia(p.created_at), p.created_by_name].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        {/* a forma fica sob o valor (como no Financeiro): na coluna estreita ela nunca é cortada */}
                        <span className="text-right">
                          <span className="block text-[15px] font-medium tabular-nums">{formatBRL(p.total)}</span>
                          <span className="block text-[12px] text-gray-500">{rotuloFormas(p)}</span>
                        </span>
                        <span className="col-span-2 flex min-w-0 items-center gap-2">
                          <WorkflowChip wf={effectiveWorkflow(p)} pedido={p} className="shrink-0" />
                          <span className="truncate text-[12.5px] text-gray-500">{situacao(p)}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
                {pedidos.length >= LIMITE_LISTA && (
                  <li className="px-5 py-4 text-[12.5px] text-gray-500">
                    Mostrando os {LIMITE_LISTA} mais recentes. Use a busca para encontrar pedidos anteriores.
                  </li>
                )}
              </ul>
            )}
          </Card>

          <Card ref={docRef} className="xl:sticky xl:top-5 xl:max-h-[calc(100vh-2.5rem)] xl:overflow-y-auto">
            {selecionado ? (
              <DocumentoPedido
                key={selecionado.id}
                pedido={selecionado}
                onEditar={() => navigate(`/pedidos/editar/${selecionado.id}`)}
                onEstornar={(p) => setEstornoId(p.id)}
                onChanged={() => { fetchPedidos(); setKpisVersao((v) => v + 1); }}
              />
            ) : (
              <p className="p-7 text-sm text-gray-500">{loading ? 'Carregando.' : 'Selecione um pedido na lista.'}</p>
            )}
          </Card>
        </div>
      </PageBody>

      <EstornoDialog
        order={pedidos.find((p) => p.id === estornoId) || null}
        open={Boolean(estornoId)}
        onOpenChange={(aberto) => { if (!aberto) setEstornoId(null); }}
        onDone={() => { fetchPedidos(); setKpisVersao((v) => v + 1); }}
      />
    </>
  );
};

export default OrderManagement;
